/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { logger } from '@nykaa/logger';
import { pushEvent } from '@nykaa/data-layer';
import OTPLogin from '@nykaa/auth/components/OTPLogin';
import doLogin from '@nykaa/auth-shared/store/actions/login';
import createAccount from '@nykaa/auth-shared/store/actions/createAccount';
import doSendOtp from '@nykaa/auth-shared/store/actions/sendOtp';
import Toast, { VARIANT } from '@nykaa/ui-components/Toast';
import { SendOtpProps, RegisterProps } from '@nykaa/auth-shared/store/types';
import {
  isPhoneNumber,
  getCurrentStep,
  getLoginWelcomeMessage,
  getSplittedName,
  handleShareEvent,
  isEmailLogin,
  checkOriginMobile,
} from '@nykaa/auth-shared/utils';
import { LoginServiceParams } from '@nykaa/auth-shared/store/types';
import {
  COUNTER_DURATION_IN_SECONDS,
  PASSWORD,
  TNC_URL,
  LIMIT_ERROR_CODE,
  OTP_LOGIN_ERROR,
  SEND_OTP_CAPTCHA_ERROR,
  TOAST_TIMER,
  OTP_REGISTER_ERROR,
  AUTH_EVENTS,
} from '@nykaa/auth-shared/constants';
import {
  API_PARAM_KEYS,
  SOMETHING_WENT_WRONG,
  CAPTCHA_TYPE,
  G_CAPTCHA_RESPONSE,
  CAPTCHA_V3,
  USER_VERSION_2,
  ERROR_SEND_OTP,
} from '@nykaa/auth-shared/store/constants';
import { SHARE_EVENTS } from '@nykaa/auth-shared/constants';
import useAuthData from '@nykaa/auth-shared/hooks/useAuthData';
import useVerificationSwitch from '@nykaa/auth-shared/hooks/useVerificationSwitch';
import { fetchAuth } from 'shared/store/auth/action';
import { removeAuthFromStorage } from 'store/auth/helper';
import { executeCaptcha } from 'helpers/utils/reCaptcha';
import { recaptchaActions } from 'User/store/resetPassword/actionTypes';
import { userSessionTypes } from 'shared/constants';
import {
  setAuthSteps,
  setRegistration,
  setMobileNumberLocal,
} from 'shared/helpers/auth';
import { VerifyScreenProps } from 'layouts/Auth/types';
import { LOGINTYPE, OTP_LOGIN_CAPTCHA_ERROR } from 'constants/auth';
import { State } from 'store/types';
import { hideSignUp } from 'store/app/actions';
import { getEligibleForVariant } from 'helpers/experimentV2';
import { AB_WEB_AUTOREAD_OTP } from 'constants/experiments';
import { deleteCartQtyLocalStorage } from 'helpers/listingPage';
import { pushBuyNowClicked } from 'layouts/Product/Actions/helper';

const { LOGIN_SUCCESS, SIGNUP_SUCCESS, LOGIN_PROCEED_CLICKED } = AUTH_EVENTS;

const OTPScreen = ({ setSuccessMessage, showNudge }: VerifyScreenProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    origin,
    mobile,
    email,
    message,
    error,
    isApiError,
    linkedEmail,
    sendOtpToMail,
    steps,
    selectedCustomerId,
    isExists,
    emailList,
    name,
  } = useAuthData();
  const {
    sendOtpCaptcha: isOtpCaptchaEnabled,
    loginCaptcha: isLoginCaptchaEnabled,
    signupCaptcha: isSignupCaptchaEnabled,
  } = useSelector((state: State) => state.remoteConfig.configs.configFlags) ||
  {};
  const {
    mobile: mobileField,
    email: emailField,
    mobileNumber,
    userName,
  } = API_PARAM_KEYS;

  const stepData = getCurrentStep(steps, history);
  const [isError, setError] = useState(error);
  const [errorMessage, setErrorMessage] = useState(message);
  const [toastError, setToastError] = useState(isApiError);
  const [toastMsg, setToastMsg] = useState(message);
  const [hideOtpTimer, setHideOtpTimer] = useState(false);
  const [otpStatus, setOtpStatus] = useState(false);
  const [resendTimer, setResendTimer] = useState(COUNTER_DURATION_IN_SECONDS);
  const isPasswordExist = stepData?.switchTo === PASSWORD;

  const webOtpAutoReadEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_WEB_AUTOREAD_OTP, 'A')
  );
  const isOtpAutoFillEnable = useSelector(
    (state: State) => state.remoteConfig.switches.isOtpAutoFillEnabled
  );

  let emailPhone =
    origin !== mobileNumber || selectedCustomerId ? email : mobile;
  if (sendOtpToMail) emailPhone = linkedEmail || email;

  const { handleSwitch } = useVerificationSwitch(false);

  useEffect(() => {
    setToastError(isApiError);
    setToastMsg(message);
  }, [isApiError]);

  useEffect(() => {
    setError(error);
    setErrorMessage(message);
  }, [error]);

  const handleDoSendOtp = useCallback(
    (sendOtpParams: SendOtpProps) => {
      doSendOtp(sendOtpParams)(dispatch)
        .then((response) => {
          const { otpStatus: responseOtpStatus, error: otpError } =
            response || {};
          if (responseOtpStatus) {
            setOtpStatus(responseOtpStatus);
            setResendTimer(COUNTER_DURATION_IN_SECONDS);
            pushEvent(LOGIN_PROCEED_CLICKED);
          } else if (otpError == LIMIT_ERROR_CODE) setHideOtpTimer(true);
        })
        .catch((err: Error) => {
          logger.error(err, ERROR_SEND_OTP);
          setResendTimer(0);
        });
    },
    [dispatch]
  );

  const handleSendOTP = useCallback(() => {
    setToastError(false);
    setResendTimer(0);
    let sendOtpParams: SendOtpProps = {
      history,
      emailPhone,
      customerId: selectedCustomerId,
    };
    if (isOtpCaptchaEnabled) {
      executeCaptcha(recaptchaActions.SEND_OTP)
        .then((token: string) => {
          const captchaParams = {
            captchaResponse: token,
          };
          sendOtpParams = {
            ...captchaParams,
            ...sendOtpParams,
          };
          handleDoSendOtp(sendOtpParams);
        })
        .catch((err: Error) => {
          logger.error(err, SEND_OTP_CAPTCHA_ERROR);
          setToastError(true);
          setToastMsg(err.message);
        });
    } else {
      handleDoSendOtp(sendOtpParams);
    }
  }, [
    emailPhone,
    handleDoSendOtp,
    history,
    isOtpCaptchaEnabled,
    selectedCustomerId,
  ]);

  useEffect(() => {
    handleSendOTP();
  }, []);

  const handleDoLogin = useCallback(
    (params: LoginServiceParams) => {
      doLogin(params)(dispatch)
        .then((response) => {
          const { success, firstname } = response || {};
          const loginType = checkOriginMobile(origin)
            ? LOGINTYPE.PHONE
            : LOGINTYPE.EMAIL;
          if (success) {
            handleShareEvent(SHARE_EVENTS.login, dispatch);
            setMobileNumberLocal(mobile);
            deleteCartQtyLocalStorage();
            if (selectedCustomerId) {
              setAuthSteps(1);
            } else {
              setAuthSteps(6);
            }
            setTimeout(() => {
              fetchAuth()(dispatch).then(() => {
                if (showNudge) {
                  dispatch(hideSignUp(history));
                  pushBuyNowClicked(false);
                }
                pushEvent(LOGIN_SUCCESS, {
                  loginType,
                  loginStatus: userSessionTypes.LOGGED_IN,
                });
              });
            }, 500);
            setSuccessMessage(getLoginWelcomeMessage(firstname));
          } else {
            setErrorMessage(response?.message || SOMETHING_WENT_WRONG);
            setError(true);
          }
        })
        .catch((err: Error) => {
          setToastError(true);
          setToastMsg(SOMETHING_WENT_WRONG);
          logger.error(err, OTP_LOGIN_ERROR);
        });
    },
    [dispatch, mobile, selectedCustomerId, setSuccessMessage]
  );

  const loginCallback = useCallback(
    (fieldName, otp) => {
      let params: LoginServiceParams = {
        [fieldName]: emailPhone,
        otp,
      };
      if (selectedCustomerId) params.customerId = selectedCustomerId;
      setError(false);
      setToastError(false);
      if (isLoginCaptchaEnabled) {
        executeCaptcha(recaptchaActions.LOGIN)
          .then((token: string) => {
            const captchaParams = {
              captchaResponse: token,
            };
            params = {
              ...captchaParams,
              ...params,
            };
            handleDoLogin(params);
          })
          .catch((err: Error) => {
            setToastError(true);
            setToastMsg(SOMETHING_WENT_WRONG);
            logger.error(err, OTP_LOGIN_CAPTCHA_ERROR);
          });
      } else handleDoLogin(params);
    },
    [emailPhone, handleDoLogin, isLoginCaptchaEnabled, selectedCustomerId]
  );

  const handleDoSignup = useCallback(
    (regParams: RegisterProps) => {
      const { firstName, lastName } = getSplittedName(name || '');
      if (firstName) {
        regParams.firstname = firstName;
        regParams.lastname = lastName;
      }
      if (sendOtpToMail && !linkedEmail) regParams.mobile_number = mobile;
      if (email) regParams.email = email;
      createAccount(regParams)(dispatch).then((response) => {
        const { success, sign_up_source } = response;
        const loginType = isEmailLogin(sign_up_source)
          ? LOGINTYPE.EMAIL
          : LOGINTYPE.PHONE;
        if (success) {
          handleShareEvent(SHARE_EVENTS.signup, dispatch);
          setMobileNumberLocal(mobile);
          setTimeout(() => {
            fetchAuth()(dispatch).then(() => {
              pushEvent(SIGNUP_SUCCESS, {
                loginType,
                loginStatus: userSessionTypes.LOGGED_IN,
              });
              if (showNudge) {
                dispatch(hideSignUp(history));
                pushBuyNowClicked(false);
                if (!email) {
                  setRegistration();
                }
                window.location.reload();
              } else {
                if (!email) {
                  setRegistration();
                }
              }
            });
          }, 500);
        } else {
          setErrorMessage(response?.message || SOMETHING_WENT_WRONG);
          setError(true);
        }
      });
    },
    [dispatch, email, linkedEmail, mobile, name, sendOtpToMail, showNudge]
  );

  const signup = useCallback(
    (isMobileOrigin, fieldName, otp) => {
      setError(false);
      let regParams: RegisterProps = {
        origin: isMobileOrigin && !email ? mobileField : emailField,
        otp,
        [fieldName]: emailPhone,
        user_version: USER_VERSION_2,
      };
      if (isSignupCaptchaEnabled) {
        executeCaptcha(recaptchaActions.REGISTER)
          .then((token: string) => {
            const captchaParams = {
              [G_CAPTCHA_RESPONSE]: token,
              [CAPTCHA_TYPE]: CAPTCHA_V3,
            };
            regParams = {
              ...captchaParams,
              ...regParams,
            };
            handleDoSignup(regParams);
          })
          .catch((err: Error) => {
            logger.error(err, OTP_REGISTER_ERROR);
            setToastError(true);
            setToastMsg(err.message);
          });
      } else handleDoSignup(regParams);
    },
    [
      email,
      emailField,
      emailPhone,
      handleDoSignup,
      isSignupCaptchaEnabled,
      mobileField,
    ]
  );

  const handleLogin = useCallback(
    (otp: string) => {
      const isMobileOrigin = isPhoneNumber(emailPhone);
      let fieldName = userName;
      if (isMobileOrigin) fieldName = mobileNumber;

      removeAuthFromStorage();
      if (
        !isExists ||
        (isExists && emailList?.length > 0 && !selectedCustomerId)
      ) {
        signup(isMobileOrigin, fieldName, otp);
      } else {
        loginCallback(fieldName, otp);
      }
    },
    [
      emailPhone,
      isExists,
      loginCallback,
      signup,
      emailList,
      selectedCustomerId,
      mobileNumber,
      userName,
    ]
  );
  const inputError = isError || error ? errorMessage : '';

  return (
    <>
      <OTPLogin
        source={emailPhone}
        verifyOTPCallback={handleLogin}
        sendOTP={handleSendOTP}
        tncUrl={TNC_URL}
        error={inputError}
        otpTimer={resendTimer}
        isPasswordExist={isPasswordExist}
        switchBtnCallback={handleSwitch}
        hideOtpTimer={hideOtpTimer}
        otpStatus={otpStatus}
        isNudge={showNudge}
        webOtpAutoRead={webOtpAutoReadEnabled}
        isOtpAutoFillEnable={isOtpAutoFillEnable}
      />
      {toastError && (
        <Toast
          timer={TOAST_TIMER}
          variant={VARIANT.single}
          message={toastMsg}
        />
      )}
    </>
  );
};

export default OTPScreen;
